import { errorMessageFor } from './errorMessages.js'

/**
 * Thin fetch wrapper for the owner + staff API surface (`/api/owner/*`,
 * `/api/staff/*`). Mirrors admin's `adminApi`: JSON encoding, cookie
 * credentials, and turning the API's `{ error: { code, message } }` shape into
 * a typed `OwnerApiError` whose `.userMessage` is ready to display.
 *
 * Paths are passed in full under `/api` (e.g. `/owner/auth/login`,
 * `/staff/login`) because owner and staff routes don't share a single prefix.
 * A `multipart` escape supports the logo upload (FormData body, no JSON
 * Content-Type header).
 */

export class OwnerApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, unknown>

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'OwnerApiError'
    this.status = status
    this.code = code
    if (details !== undefined) this.details = details
  }

  /** Human string driven by the error-code dictionary. */
  get userMessage(): string {
    return errorMessageFor(this.code)
  }
}

interface ApiErrorShape {
  error?: { code?: string; message?: string; details?: Record<string, unknown> }
}

async function request<T>(
  path: string,
  init?: RequestInit & { json?: unknown; multipart?: FormData },
): Promise<T> {
  const { json, multipart, headers, ...rest } = init ?? {}
  let res: Response
  try {
    res = await fetch(`/api${path}`, {
      credentials: 'include',
      headers: {
        // multipart bodies must NOT carry a manual Content-Type — the browser
        // sets it with the boundary. JSON bodies get the explicit header.
        ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
      ...(multipart !== undefined ? { body: multipart } : {}),
      ...rest,
    })
  } catch {
    throw new OwnerApiError(0, 'NETWORK_ERROR', 'Network request failed')
  }

  if (res.status === 204) return undefined as T

  let body: unknown = null
  const text = await res.text()
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = null
    }
  }

  if (!res.ok) {
    const err = (body as ApiErrorShape | null)?.error
    throw new OwnerApiError(
      res.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? `Request failed (${res.status})`,
      err?.details,
    )
  }

  return body as T
}

export const ownerApi = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: 'POST', json }),
  patch: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: 'PATCH', json }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Upload a multipart/form-data body (logo upload). */
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', multipart: form }),
}
